import { InteractionStatus } from "@azure/msal-browser";
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { Providers } from "@microsoft/mgt-element";
import { Msal2Provider } from "@microsoft/mgt-msal2-provider";
import "bootstrap/dist/css/bootstrap.min.css";
import { FC, useEffect, useState } from "react";
import CacheBuster from "react-cache-buster";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { version } from "../package.json";
import { getEnv } from "./AppConfigs";
import { AppRoutes } from "./AppRoutes";
import { fetchConfigs } from "./actions/configActions";
import { fetchEmployees } from "./actions/employeeActions";
import { fetchGroups } from "./actions/groupsActions";
import { fetchGroupsOfUser } from "./actions/groupsOfUserActions";
import { fetchLookupData } from "./actions/lookupDataActions";
import { fetchMailTemplates, fetchSoftwares } from "./actions/systemMgmtAction";
import { Notifier } from "./components/notifier";
import { msalScopes } from "./constants/azureAuthConstants";
import { Alert } from "./modules/alert";
import { Button } from "./modules/button";
import { Icon } from "./modules/icon";
import { SET_USER } from "./reducers/user.reducer";
import { SET_EXPIRED_TOKEN_ERROR, SET_UNAUTHORIZED_ERROR } from "./services/main/main.reducer";
import { msalInstance } from "./services/msal";
import { persistor, store, useSelector } from "./store/store";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { fetchAdminUser } from "./actions/adminUserAction";
import { handleOneSignalLogin } from "./actions/onesignal";
import { Environment } from "./constants";
import { MineInfoContextProvider } from "./contexts/mine-info-context";
import { getAppEnv } from "./helper/util";
import { useSoftwares } from "./hook";
import ReactQueryProvider from "./store/react-query";
import { LoginPage } from "./components/LoginPage";

if (getAppEnv() === Environment.production) console.log = () => { };

const Root: FC = (props) => {
  const { instance, accounts, inProgress } = useMsal();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [errorMessage, setErrorMessage] = useState("");
  const mainState = useSelector((state) => state.main);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const user = useSelector((state) => state.user);

  const [authLoading, setAuthLoading] = useState(true);

  // get all essential values
  useSoftwares();

  const initMsal2Provider = () => {
    Providers.globalProvider = new Msal2Provider({
      clientId: getEnv("REACT_APP_AZURE_AUTH_CLIENT_ID"),
      scopes: msalScopes,
    });
  };

  const initialize = async (userData: any) => {
    try {
      initMsal2Provider();

      // Xử lý OneSignal khi user login
      const userId = userData?.localAccountId;
      if (userId) {
        handleOneSignalLogin(userId).catch((err: any) => {
          console.error("OneSignal login error:", err);
        });
      }

      await Promise.all([
        // UserService.auth(),
        fetchConfigs(),
        fetchGroupsOfUser(userData?.localAccountId),
        fetchAdminUser(userData?.localAccountId),
        fetchEmployees(),
        fetchGroups(),
        fetchSoftwares(),
        fetchMailTemplates(),
        fetchLookupData(),
      ]);
    } catch (error: any) {
      console.error(error);
      setErrorMessage(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const retrieveAccessToken = (mainStateType: any = null) => {
    const request = {
      account: accounts[0],
      scopes: msalScopes,
      forceRefresh: true,
    };

    setAuthLoading(true);

    // Retrieve an access token
    instance
      .acquireTokenSilent(request)
      .then((response) => {
        if (response.accessToken) {
          const data = {
            ...accounts[0],
            accessToken: response.accessToken,
            expiresOn: response.expiresOn,
          };
          store.dispatch({ type: SET_USER, data });
          if (mainStateType !== null) {
            store.dispatch({ type: mainStateType, status: false });
          }

          if (!mainStateType) {
            if (mainState.isExpiredToken) {
              store.dispatch({ type: SET_EXPIRED_TOKEN_ERROR, status: false });
            }
            if (mainState.isUnauthorized) {
              store.dispatch({ type: SET_UNAUTHORIZED_ERROR, status: false });
            }
            initialize(data);
          }

          return response.accessToken;
        }
        return null;
      })
      .catch((error) => {
        setErrorMessage(error.message);

        setAuthLoading(false);

        if (error.errorCode === "interaction_required" || error.errorCode === "login_required") {
          instance.loginRedirect();
          return;
        }
      });
  };

  // const loginViagsAccount = async () => {
  //   const formData: FormData = new FormData();
  //   formData.append("UserCode", userCodeViags!);
  //   formData.append("Password", passwordViags!);
  //   formData.append("Language", "VN");
  //   formData.append("Company_ID", "2");
  //   formData.append("Token", tokenViags!);
  //   const url = `${hostViags}/System/UserLogin`;
  //   const response = await Axios.post(url, formData, {
  //     headers: { "Content-Type": "multipart/form-data" },
  //   });
  //   console.log("response login", response);
  // };

  const login = async () => {
    instance.loginRedirect();
  };

  useEffect(() => {
    // eslint-disable-next-line eqeqeq
    if (inProgress == InteractionStatus.None && accounts.length > 0) {
      retrieveAccessToken();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, accounts, instance]);

  useEffect(() => {
    if (!mainState.isExpiredToken) return;

    // eslint-disable-next-line eqeqeq
    if (inProgress == InteractionStatus.None && accounts.length > 0) {
      retrieveAccessToken(SET_EXPIRED_TOKEN_ERROR);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainState.isExpiredToken]);

  useEffect(() => {
    if (!mainState.isUnauthorized) return;

    // eslint-disable-next-line eqeqeq
    if (inProgress == InteractionStatus.None && accounts.length > 0) {
      retrieveAccessToken(SET_UNAUTHORIZED_ERROR);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainState.isUnauthorized]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  // if (authLoading) return <LoadingSkeleton />;

  return (
    <div className="Wraper">
      <title>{getEnv("REACT_APP_APP_NAME")}</title>
      {/* <LoadingSkeleton></LoadingSkeleton> */}
      <AuthenticatedTemplate>{user && props.children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </div>
  );
};

const appEnv = getEnv("REACT_APP_ENV");
const isEnabledCacheBuster = appEnv === "uat" || appEnv === "production";

function App() {
  return (
    <ReactQueryProvider>
      <MsalProvider instance={msalInstance}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <title>{getEnv("REACT_APP_APP_NAME")}</title>
            <Alert />
            <Notifier />
            <CacheBuster
              currentVersion={version}
              isEnabled={isEnabledCacheBuster} //If false, the library is disabled.
              isVerboseMode={false} //If true, the library writes verbose logs to console.
              loadingComponent={<Icon.Loading />} //If not pass, nothing appears at the time of new version check.
              metaFileDirectory={"."} //If public assets are hosted somewhere other than root on your server.
            >
              <Root>
                <MineInfoContextProvider>
                  <AppRoutes />
                </MineInfoContextProvider>
              </Root>
            </CacheBuster>
          </PersistGate>
        </Provider>
      </MsalProvider>
    </ReactQueryProvider>
  );
}

export default App;
